import { useState } from 'react';

import { useAppStore, useAppDispatch } from '../../../../app/hooks';
import { selectProposalSettings, updateProposal } from '../../../../slices/proposalsSlice';
import useShouldShowCreatedBy from '../../../collaboration/hooks/useShouldShowCreatedBy';
import CreatedByLabel from '../../../collaboration/components/CreatedByLabel/CreatedByLabel';
import { downloadPdf } from '../../../../utils/downloadFileByLink';
import GenericForm, { GenericProposalFormProps } from '../GenericForm';
import useGeneratePdf from '../useGeneratePdf';
import useProposalPdfUrl from '../useProposalPdfUrl';
import useUpdateProposal from './useUpdateProposal';
import useLinkedInvoicePrompt from '../linkedInvoice/useLinkedInvoicePrompt';
import styles from './UpdateForm.module.css';
import { FormValues } from '../subForms/MainSubForm/useMainSubForm';
import { selectContactsMap } from 'slices/contactsSlice';
import { hideCallsBatch } from 'slices/callsSlice';
import { withErrorSnackbar } from 'src/services/api/withErrorSnackbar';
import { useSnackbar } from 'notistack';
import { MessageType } from 'src/components/UI/snackbar/providers/SnackBarProvider';
import { useTranslation } from 'next-i18next';
import ProposalEntity, {
  DISCOUNT_TYPES,
  PriceProposalStatus,
  isProposalSigned,
} from 'src/db/entities/proposal/ProposalEntity';
import { generateSignLink } from 'src/api/proposalsAPI/api';

export interface UpdateFormOptions {
  data: ProposalEntity;
  onFormDataChanged?: () => void;
  onSuccess?: () => void;
}

export default function UpdateForm({ data, onFormDataChanged, onSuccess }: UpdateFormOptions) {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const shouldShowCreatedBy = useShouldShowCreatedBy(data.createdBy);
  const isSigned = isProposalSigned(data);

  const [formIsDirty, setFormIsDirty] = useState(false);

  const defaultValues: FormValues = {
    title: data.title,
    contactId: data.contactId,
    client: data.client,
    status: data.status || PriceProposalStatus.DRAFT,
    items: data.items,
    comment: data.description,
    includesTax: data.settings.includesTax,
    currency: data.settings.currency,
    discountAmount: data.discountAmount,
    discountType: data.discountType || DISCOUNT_TYPES.VALUE,
  };
  const { enqueueSnackbar } = useSnackbar();
  const { t: tProposalForm } = useTranslation('proposal-form');

  const update = useUpdateProposal();
  const { offerInvoiceUpdate, dialogUI: linkedInvoiceDialogUI } = useLinkedInvoicePrompt();

  const getPdfUrl = useProposalPdfUrl(useGeneratePdf());

  const preparePayloadForUpdate = (formValues: FormValues): ProposalEntity => {
    const settings = selectProposalSettings(store.getState());

    return {
      ...data,
      contactId: formValues.contactId,
      client: formValues.client,
      title: formValues.title,
      status: formValues.status,
      items: formValues.items,
      description: formValues.comment,
      discountAmount: formValues.discountAmount,
      discountType: formValues.discountType,
      settings: {
        ...settings,
        currency: formValues.currency,
        includesTax: formValues.includesTax,
      },
    };
  };

  const handleSubmit: GenericProposalFormProps['onSubmit'] = async (formValues) => {
    if (isSigned) {
      return;
    }
    withErrorSnackbar({
      onTry: async () => {
        const contactsMap = selectContactsMap(store.getState());
        const contact = contactsMap[formValues.contactId];
        const contactPhones = contact.phones.map(({ normalized_phone }) => normalized_phone);
        dispatch(hideCallsBatch(contactPhones));
      },
      enqueueSnackbar,
    });
    const params = preparePayloadForUpdate(formValues);
    return update(params, async (saved) => {
      await offerInvoiceUpdate(saved);
      onSuccess?.();
    });
  };

  const handleDownload: GenericProposalFormProps['onDownload'] = (formValues) => {
    withErrorSnackbar({
      onTry: async () => {
        const contactsMap = selectContactsMap(store.getState());
        const contact = contactsMap[formValues.contactId];
        const contactPhones = contact.phones.map(({ normalized_phone }) => normalized_phone);
        dispatch(hideCallsBatch(contactPhones));
      },
      enqueueSnackbar,
    });
    const handleSuccess = async (data: ProposalEntity) => {
      const tempUrl = await getPdfUrl(data);

      if (data.isDraft && !isSigned) {
        await dispatch(
          updateProposal({ ...data, isDraft: false, updatedAt: Date.now(), sentAt: Date.now() })
        );
      }

      if (tempUrl) {
        await downloadPdf(tempUrl, data.title);
      }
    };

    if (formIsDirty && !isSigned) {
      const params = preparePayloadForUpdate(formValues);

      return update(params, async (saved) => {
        await offerInvoiceUpdate(saved);
        await handleSuccess(saved);
      });
    } else {
      return handleSuccess(data);
    }
  };

  const handlePreview: GenericProposalFormProps['onPreview'] = async (formValues) => {
    const params = preparePayloadForUpdate(formValues);
    const tempUrl = await getPdfUrl(params);

    if (tempUrl) {
      window.open(tempUrl);
    }
  };

  const handleShareLink = () => {
    const sub = generateSignLink(data.uuid).subscribe({
      next: async (url) => {
        await navigator.clipboard.writeText(url);
        enqueueSnackbar({ message: tProposalForm('signatureLinkCopied'), variant: MessageType.Success });
      },
      error: () => {
        enqueueSnackbar({ message: tProposalForm('signatureLinkError'), variant: MessageType.Error });
      },
      complete: () => {
        sub.unsubscribe();
      }
    });
  };

  const handleFormDataChanged = () => {
    setFormIsDirty(true);

    onFormDataChanged();
  };

  return (
    <div className={styles.wrapper}>
      <GenericForm
        defaultValues={defaultValues}
        contactPinned={true}
        onFormDataChanged={handleFormDataChanged}
        onDownload={handleDownload}
        onPreview={handlePreview}
        onSubmit={handleSubmit}
        onSign={handleShareLink}
        readOnly={isSigned}
      />
      {shouldShowCreatedBy && (
        <CreatedByLabel
          className={styles.createdBy}
          email={data.createdBy}
          createdAt={data.createdAt}
        />
      )}
      {linkedInvoiceDialogUI}
    </div>
  );
}
